import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Avatar as MuiAvatar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services/userService';
import { avatarService } from '../../services/avatarService';
import { badgeService } from '../../services/badgeService';
import { pointsService } from '../../services/pointsService';
import { teamService } from '../../services/teamService';
import { User, UserAvatar, UserBadge, Badge, PersonalRecord, Points, Team, ProfilePrivacy } from '../../types';
import ReadyPlayerMeAvatar from './ReadyPlayerMeAvatar';
import { formatPRTimeDisplay } from '../../utils/prTimeFormat';

const PublicProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canView, setCanView] = useState(false);
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [privacy, setPrivacy] = useState<ProfilePrivacy | null>(null);
  const [userAvatar, setUserAvatar] = useState<UserAvatar | null>(null);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [availableBadges, setAvailableBadges] = useState<Badge[]>([]);
  const [userPRs, setUserPRs] = useState<PersonalRecord[]>([]);
  const [userPoints, setUserPoints] = useState<Points | null>(null);
  const [userTeams, setUserTeams] = useState<Team[]>([]);

  useEffect(() => {
    loadPublicProfile();
  }, [userId, currentUser?.id]);

  const loadPublicProfile = async () => {
    if (!userId) {
      setError('Invalid profile URL');
      setLoading(false);
      return;
    }

    // If no current user, they can only view public profiles
    const viewerId = currentUser?.id || 'anonymous';

    try {
      setLoading(true);
      setError('');

      // Check if viewer can see this profile
      const profileResult = await userService.getPublicProfile(userId, viewerId);

      if (!profileResult.canView) {
        setCanView(false);
        setError(profileResult.reason || 'You do not have permission to view this profile');
        setLoading(false);
        return;
      }

      setCanView(true);
      setProfileUser(profileResult.user);
      setPrivacy(profileResult.privacy || null);

      // Load profile data based on privacy settings
      if (profileResult.user && profileResult.privacy) {
        await loadProfileData(profileResult.user, profileResult.privacy);
      }
    } catch (err: any) {
      console.error('Error loading public profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadProfileData = async (user: User, privacy: ProfilePrivacy) => {
    try {
      // Get user's teams first
      const teams = await teamService.getTeamsByAthlete(user.id);
      setUserTeams(teams);

      if (teams.length === 0) {
        return;
      }

      const teamId = teams[0].id;

      // Load data based on privacy settings
      const promises = [];

      if (privacy.showAvatar) {
        promises.push(
          avatarService.getUserAvatar(user.id, teamId)
            .then(avatar => setUserAvatar(avatar))
            .catch(err => console.warn('Failed to load avatar:', err))
        );
      }

      if (privacy.showBadges) {
        promises.push(
          badgeService.getUserBadges(user.id, teamId)
            .then(badges => setUserBadges(badges))
            .catch(err => console.warn('Failed to load badges:', err)),
          badgeService.getAvailableBadges(teamId)
            .then(badges => setAvailableBadges(badges))
            .catch(err => console.warn('Failed to load available badges:', err))
        );
      }

      if (privacy.showPRs) {
        promises.push(
          avatarService.getUserPRs(user.id, teamId)
            .then(prs => setUserPRs(prs))
            .catch(err => console.warn('Failed to load PRs:', err))
        );
      }

      if (privacy.showStats) {
        promises.push(
          pointsService.getUserPoints(user.id, teamId)
            .then(points => setUserPoints(points))
            .catch(err => console.warn('Failed to load points:', err))
        );
      }

      await Promise.allSettled(promises);
    } catch (err) {
      console.error('Error loading profile data:', err);
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'default';
      case 'rare': return 'primary';
      case 'epic': return 'secondary';
      case 'legendary': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!canView || error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/')}
          variant="outlined"
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!profileUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Profile not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        {currentUser && (
          <Button
            variant="outlined"
            onClick={() => navigate('/?page=teams')}
          >
            Back to Team Roster
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {profileUser.displayName}'s Profile
        </Typography>
        <Chip 
          label={profileUser.role === 'coach' ? 'Coach' : 'Athlete'} 
          color="primary" 
          size="small" 
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/profile/${profileUser.id}/dashboard`)}
        >
          View Dashboard
        </Button>
      </Box>

      {/* Avatar Section */}
      {false && privacy?.showAvatar && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Avatar
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              minHeight: 400,
              background: 'linear-gradient(135deg, #87CEEB 0%, #98FB98 50%, #F0E68C 100%)',
              borderRadius: 2,
              p: 3
            }}>
              {userAvatar?.readyPlayerMeUrl ? (
                <ReadyPlayerMeAvatar
                  userAvatar={userAvatar}
                  userId={profileUser.id}
                  userName={profileUser.displayName}
                  size={350}
                  showCreateButton={false}
                  showRefreshButton={false}
                  isContainerDragging={false}
                  onAvatarRotatingChange={() => {}} // Add empty function to match regular profile
                />
              ) : (
                <MuiAvatar
                  sx={{ 
                    width: 200, 
                    height: 200,
                    fontSize: 80
                  }}
                >
                  {profileUser.displayName.charAt(0).toUpperCase()}
                </MuiAvatar>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Stats Section */}
      {privacy?.showStats && userPoints && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Stats
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Level
                </Typography>
                <Typography variant="h5" color="primary">
                  {userPoints.level}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Points
                </Typography>
                <Typography variant="h5" color="primary">
                  {userPoints.walletPoints}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Current Streak
                </Typography>
                <Typography variant="h5" color="primary">
                  {userPoints.currentStreak} days
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Longest Streak
                </Typography>
                <Typography variant="h5" color="primary">
                  {userPoints.longestStreak} days
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Badges Section */}
      {privacy?.showBadges && userBadges.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Badges & Achievements
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userBadges.map((userBadge) => {
                const badge = availableBadges.find(b => b.id === userBadge.badgeId);
                return (
                  <Chip
                    key={userBadge.id}
                    icon={<TrophyIcon />}
                    label={badge?.name || 'Unknown Badge'}
                    color={getRarityColor(badge?.rarity || 'common') as any}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Personal Records Section */}
      {privacy?.showPRs && userPRs.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Personal Records
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {userPRs.map((pr) => (
                <Box key={pr.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {pr.eventType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pr.location && `${pr.location} • `}
                      {new Date(pr.date.seconds * 1000).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary">
                    {formatPRTimeDisplay(pr.time)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Team Section */}
      {privacy?.showTeam && userTeams.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Teams
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userTeams.map((team) => (
                <Chip
                  key={team.id}
                  label={team.name}
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default PublicProfilePage;
